/**
 * UI STORE
 * 
 * Manages global UI state like theme, zoom, camera position.
 * Persists theme preference to localStorage.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

interface UIState {
  theme: "light" | "dark";
  zoom: number;
  cameraX: number;
  cameraY: number;
  isPanning: boolean;
  showGrid: boolean;
  showZoneLabels: boolean;
  showAgentCounts: boolean;
  editMode: boolean;
  commandPaletteOpen: boolean;
  agentsViewMode: "list" | "board";

  setTheme: (theme: "light" | "dark") => void;
  toggleTheme: () => void;
  setZoom: (zoom: number) => void;
  setCamera: (x: number, y: number) => void;
  setPanning: (isPanning: boolean) => void;
  toggleGrid: () => void;
  toggleZoneLabels: () => void;
  toggleAgentCounts: () => void;
  resetCamera: () => void;
  setEditMode: (editMode: boolean) => void;
  toggleEditMode: () => void;
  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;
  setAgentsViewMode: (mode: "list" | "board") => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: "dark",
      zoom: 1.4,
      cameraX: 0,
      cameraY: 50,
      isPanning: false,
      showGrid: true,
      showZoneLabels: true,
      showAgentCounts: true,
      editMode: false,
      commandPaletteOpen: false,
      agentsViewMode: "list" as "list" | "board",

      setTheme: (theme) => {
        if (theme === "dark") {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
        set({ theme });
      },

      toggleTheme: () =>
        set((state) => {
          const newTheme = state.theme === "dark" ? "light" : "dark";
          if (newTheme === "dark") {
            document.documentElement.classList.add("dark");
          } else {
            document.documentElement.classList.remove("dark");
          }
          return { theme: newTheme };
        }),

      setZoom: (zoom) => set({ zoom: Math.max(0.3, Math.min(3, zoom)) }),
      setCamera: (cameraX, cameraY) => set({ cameraX, cameraY }),
      setPanning: (isPanning) => set({ isPanning }),
      toggleGrid: () => set((s) => ({ showGrid: !s.showGrid })),
      toggleZoneLabels: () => set((s) => ({ showZoneLabels: !s.showZoneLabels })),
      toggleAgentCounts: () => set((s) => ({ showAgentCounts: !s.showAgentCounts })),
      resetCamera: () => set({ cameraX: 0, cameraY: 50, zoom: 1.4 }),
      setEditMode: (editMode) => set({ editMode }),
      toggleEditMode: () => set((s) => ({ editMode: !s.editMode })),
      setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
      toggleCommandPalette: () => set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
      setAgentsViewMode: (agentsViewMode) => set({ agentsViewMode }),
    }),
    {
      name: "factory-ui-preferences",
      version: 2,
      migrate: (persisted: any, version: number) => {
        if (version < 2) {
          return { ...persisted, agentsViewMode: "list" };
        }
        return persisted as any;
      },
      partialize: (state) => ({
        theme: state.theme,
        showGrid: state.showGrid,
        showZoneLabels: state.showZoneLabels,
        showAgentCounts: state.showAgentCounts,
        agentsViewMode: state.agentsViewMode,
      }),
    }
  )
);
